import React, { useEffect, useState } from 'react';
import { Modal } from '../Modal';
import { TableSkeleton } from '../Skeleton';
import { useToast } from '../../contexts/ToastContext';
import { RemoteSessionRecord, remoteSupportApi, useRemoteSupportMe } from '../../services/remoteSupportService';
import { endReasonLabel, filesLabel, formatDuration, formatSessionTime } from '../../utils/remoteSupportLabels';
import { useRovingTabs } from '../../hooks/useRovingTabs';

/**
 * 설정의 '원격지원 기록' — 내 PC에서 있었던 원격지원(누가·언제·얼마나·제어 시간·처리 메모).
 * 원격지원 담당자에게는 '내가 지원한 기록' 탭이 더 있고, 거기서 처리 메모를 고칠 수 있다.
 *
 * 직원 본인에게 보여주는 이유: 동의하고 참여한 세션의 기록이라 감사 조회 통지(super_admin 전용)와 성격이 다르다.
 */

type Tab = 'mine' | 'operator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const MyRemoteSessionsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const me = useRemoteSupportMe();
  const [tab, setTab] = useState<Tab>('mine');
  const [rows, setRows] = useState<RemoteSessionRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const tabKeys: Tab[] = me.isHelpdesk ? ['mine', 'operator'] : ['mine'];
  const tabs = useRovingTabs<Tab>(tabKeys, tab, setTab);

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    setRows(null);
    setError(null);
    setEditing(null);
    (tab === 'mine' ? remoteSupportApi.mySessions() : remoteSupportApi.operatorSessions())
      .then((r) => {
        if (!ignore) setRows(r);
      })
      .catch((err) => {
        if (!ignore) setError(err instanceof Error ? err.message : '원격지원 기록을 불러오지 못했습니다.');
      });
    return () => {
      ignore = true;
    };
  }, [isOpen, tab]);

  const saveNote = async (code: string) => {
    setSaving(true);
    try {
      await remoteSupportApi.saveNote(code, noteDraft);
      setRows((prev) => prev?.map((r) => (r.sessionCode === code ? { ...r, note: noteDraft.trim() || null } : r)) ?? prev);
      setEditing(null);
      showToast({ type: 'success', message: '처리 메모를 저장했습니다.' });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '메모를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="원격지원 기록" size="lg" bodyFlex>
      {me.isHelpdesk && (
        <div {...tabs.tablistProps} className="flex gap-1 mb-3 border-b border-slate-200">
          {tabKeys.map((key) => (
            <button
              key={key}
              type="button"
              {...tabs.tabProps(key)}
              className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
                tab === key ? 'border-brand-600 text-brand-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {key === 'mine' ? '내 PC 기록' : '내가 지원한 기록'}
            </button>
          ))}
        </div>
      )}

      <div {...tabs.panelProps(tab)} className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <p className="py-8 text-center text-sm text-slate-500">{error}</p>
        ) : !rows ? (
          <TableSkeleton rows={3} cols={3} />
        ) : rows.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-slate-500">
              {tab === 'mine' ? '아직 이 계정으로 받은 원격지원이 없습니다' : '아직 지원한 기록이 없습니다'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {tab === 'mine'
                ? '담당자 프로필의 원격지원 요청으로 도움을 받으면 담당자와 시간이 여기에 남습니다'
                : '직원의 원격지원 요청을 수락하거나 직원 프로필의 원격지원 시작으로 지원할 수 있습니다'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.sessionCode} className="py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium text-slate-800">
                    {tab === 'mine' ? `${r.operatorName} 담당자` : `${r.hostName}님 PC`}
                    {r.initiatedBy === 'host' && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-slate-100 text-slate-600 align-middle">
                        {tab === 'mine' ? '내가 요청' : '직원 요청'}
                      </span>
                    )}
                  </p>
                  <p className="shrink-0 text-xs text-slate-500 tabular-nums">{formatSessionTime(r.acceptedAt || r.requestedAt)}</p>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 tabular-nums">
                  총 {formatDuration(r.durationSeconds)}
                  {r.controlSeconds > 0 && ` · 제어 ${formatDuration(r.controlSeconds)}`}
                  {filesLabel(r.filesToHost, r.filesToOperator, tab === 'mine' ? 'host' : 'operator') &&
                    ` · ${filesLabel(r.filesToHost, r.filesToOperator, tab === 'mine' ? 'host' : 'operator')}`}
                  {' · '}
                  {endReasonLabel(r.endReason)}
                  {' · '}
                  {r.sessionCode}
                </p>
                {editing === r.sessionCode ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value.slice(0, 1000))}
                      rows={3}
                      placeholder="무엇을 확인하고 어떻게 해결했는지 적어 주세요"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="px-3 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-100"
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        onClick={() => saveNote(r.sessionCode)}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm rounded-lg bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
                      >
                        저장
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {r.note && (
                      <p className="mt-2 px-3 py-2 text-sm text-slate-700 bg-slate-50 rounded-lg whitespace-pre-wrap break-anywhere">
                        {r.note}
                      </p>
                    )}
                    {tab === 'operator' && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(r.sessionCode);
                          setNoteDraft(r.note || '');
                        }}
                        className="mt-1 text-xs text-brand-700 hover:underline"
                      >
                        {r.note ? '메모 수정' : '메모 추가'}
                      </button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};
